import React, { FunctionComponent, useCallback, useState } from 'react';
import {
  AdminDistributorProductDistributionInformation,
  AdminProductDistributionPlatformListing,
  CapitalTypeEnum,
  DistributionPlatform,
} from 'src/api';
import DocumentModal from 'src/libraries/document-modal';
import Button from 'src/ui/atoms/button';
import Date from 'src/ui/atoms/date';
import Grid, { Col } from 'src/ui/atoms/grid';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import Spacer from 'src/ui/atoms/spacer';
import Translate from 'src/ui/atoms/translate';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import ActionButtons from 'src/ui/molecules/action-buttons';
import BoxedContent from 'src/ui/molecules/boxed-content';
import EditDistributionInformation from '../edit-distribution-information';

export type ExtendedDistributionInformation = AdminDistributorProductDistributionInformation & {
  platformId: string;
  platformName: string;
  productDistributionPlatformListingId: string;
};

interface ListDistributionInformationsProps {
  distributionInformations: ExtendedDistributionInformation[];
  hasEditDistributionInformationPermission: boolean;
  onDistributionInformationAdded: () => void;
  listings: AdminProductDistributionPlatformListing[];
  distributionPlatformsWithEditPermission: DistributionPlatform[];
}

const ListDistributionInformations: FunctionComponent<ListDistributionInformationsProps> = ({
  distributionInformations,
  hasEditDistributionInformationPermission,
  onDistributionInformationAdded,
  listings,
  distributionPlatformsWithEditPermission,
}) => {
  const translate = useTranslateWithStringArgs();

  const [isEditDistributionInformationOpen, setEditDistributionInformationOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState<ExtendedDistributionInformation | null>(null);

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return '-';

    return value;
  };

  const getDistributionInfo = useCallback(
    (info: ExtendedDistributionInformation) => {
      const result: any = {
        distributionPlatform: info.platformName,
        complianceProductName: info.complianceProductName,
        useUnits: info.useUnits
          ? translate('distributionInformation.fields.useUnits.units')
          : translate('distributionInformation.fields.useUnits.amount'),
        unitSteps: info.unitSteps,
        selfDisclosureRequired: translate(`commonOptions.${info.selfDisclosureRequired}`),
        investmentExperienceMinScore: info.investmentExperienceMinScore
          ? info.investmentExperienceMinScore.toString()
          : null,
        serviceExperienceMinScore: info.serviceExperienceMinScore ? info.serviceExperienceMinScore.toString() : null,
        capitalType:
          info.costsvariables.capitalType === CapitalTypeEnum.DEBT
            ? translate('distributionInformation.fields.capitalType.debt')
            : translate('distributionInformation.fields.capitalType.equity'),
      };

      if (info.costsvariables.productStartDate && info.costsvariables.productEndDate) {
        result.productDate = (
          <>
            <Date>{info.costsvariables.productStartDate}</Date> - <Date>{info.costsvariables.productEndDate}</Date>
          </>
        );
      } else {
        result.runtimeYears = info.costsvariables.runtimeYears?.toString();
      }

      result.costsBrokerageNominalAmountPercent = `${info.costsvariables.costsBrokerageNominalAmountPercent}%`;
      result.grantsNominalAmountPercent = `${info.costsvariables.grantsNominalAmountPercent}%`;
      result.costsBrokerageNavPercent = `${info.costsvariables.costsBrokerageNavPercent}%`;
      result.grantsNavPercent = `${info.costsvariables.grantsNavPercent}%`;
      result.costsTransactionDepotNominalAmountPercent = `${info.costsvariables.costsTransactionDepotNominalAmountPercent}%`;
      result.costsTransactionDepotNavPercent = `${info.costsvariables.costsTransactionDepotNavPercent}%`;
      result.productImage = info.productImage ? (
        <Translate
          name="common.viewImage"
          args={[
            (text: string, key) => {
              return (
                <DocumentModal key={key} fileType="image" {...info.productImage}>
                  {text}
                </DocumentModal>
              );
            },
          ]}
        />
      ) : null;

      return result;
    },
    [translate],
  );

  return (
    <>
      <Spacer y={2} />
      {distributionInformations.map((info, index) => (
        <React.Fragment key={info.id || index}>
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true}>
            {Object.entries(getDistributionInfo(info)).map(([key, value], entryIndex) => (
              <Grid key={entryIndex}>
                <Col width={4}>
                  <Translate name={`distributionInformation.fields.${key}.label`} />
                  <Spacer y={2} />
                </Col>
                <Col width={8}>
                  <PlaceholderFallback>{formatValue(value)}</PlaceholderFallback>
                </Col>
              </Grid>
            ))}
            {hasEditDistributionInformationPermission && (
              <ActionButtons>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDataToEdit(info);
                    setEditDistributionInformationOpen(true);
                  }}
                >
                  <Translate name="common.edit" />
                </Button>
              </ActionButtons>
            )}
          </BoxedContent>
        </React.Fragment>
      ))}
      {isEditDistributionInformationOpen && dataToEdit && (
        <EditDistributionInformation
          onClose={() => setEditDistributionInformationOpen(false)}
          onDistributionInformationAdded={() => {
            setEditDistributionInformationOpen(false);
            onDistributionInformationAdded();
          }}
          listings={listings}
          distributionPlatformsWithEditPermission={distributionPlatformsWithEditPermission}
          data={dataToEdit}
        ></EditDistributionInformation>
      )}
    </>
  );
};

export default ListDistributionInformations;
